import type { PageDefinition } from "@judo/model-api";
import { type ReactNode, createContext, useContext } from "react";

/**
 * Page context type.
 */
export interface PageContextType {
	/** Current page definition */
	pageDefinition: PageDefinition;
	/** Transfer ID for this page (if applicable) */
	transferId: string | null;
	/** Page parameters */
	params: Record<string, unknown>;
	/** Whether page is in a dialog */
	isDialog: boolean;
}

const PageContext = createContext<PageContextType | null>(null);

/**
 * Hook to access page context.
 *
 * @returns PageContextType
 * @throws Error if used outside PageProvider
 */
export function usePageContext(): PageContextType {
	const context = useContext(PageContext);
	if (!context) {
		throw new Error("usePageContext must be used within PageProvider");
	}
	return context;
}

/**
 * Hook to access page context (optional).
 *
 * @returns PageContextType | null
 */
export function usePageContextOptional(): PageContextType | null {
	return useContext(PageContext);
}

/**
 * Props for PageProvider.
 */
export interface PageProviderProps {
	children: ReactNode;
	pageDefinition: PageDefinition;
	transferId?: string | null;
	params?: Record<string, unknown>;
	isDialog?: boolean;
}

/**
 * Provider for page context.
 */
export function PageProvider({
	children,
	pageDefinition,
	transferId = null,
	params = {},
	isDialog = false,
}: PageProviderProps) {
	const value: PageContextType = {
		pageDefinition,
		transferId,
		params,
		isDialog,
	};

	return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}
